import React, { useContext } from 'react'
import { useFormikContext } from 'formik'

import { appColors } from '../../../../globalStyles'

import { ActionablesContext } from '@contexts/ActionablesContext'

import { distributionTypeExplanations } from '@constants/distributionTypeExplanations'

import { Hider } from '@components/HOCs/Hider'
import { NumberFieldWithLabel, CheckboxWithLabel } from '@components/Form/Fields'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { SelectWithLabel } from '@components/Form/Fields'
import { DetailedBudget } from '@components/DetailedBudget'
import { R } from '@utils/currency'

export const JointBudgetFields = ({ config, jointBudget }) => {
  const disableDetailedBudget = useMediaQuery('(max-width:640px)')
  const { values, setFieldValue } = useFormikContext()
  const { selected_budget_type } = values
  const { selected_budget_type: currentlySelected } = jointBudget

  return (
    <div>
      <Incomes />
      <Tabs
        selectedIndex={selected_budget_type === 'simple' ? 0 : 1}
        onSelect={(index) => setFieldValue('selected_budget_type', index === 0 ? 'simple' : 'detailed')}
        className='app-tabs'
      >
        <TabList className='app-tabs__list flex flex-col xs:flex-row'>
          <BudgetTabHeader selected={currentlySelected} selectedTab={selected_budget_type} disableDetailedBudget={disableDetailedBudget} />
          <div className='flex justify-end h-fit'>
            <Tab
              className='app-tabs__list-item'
              selectedClassName='app-tabs__list-item--selected'>Simple expenses</Tab>
            {!disableDetailedBudget &&
              <Tab
                className='app-tabs__list-item'
                selectedClassName='app-tabs__list-item--selected'>Detailed expenses</Tab>
            }
          </div>
        </TabList>
        <TabPanel>
          <SimpleBudget minOldAgePension={config.minOldAgePension} />
        </TabPanel>
        {!disableDetailedBudget &&
          <TabPanel>
            <DetailedBudget
              isRetired={false}
              readOnly={false}
              budgetCategories={config.budgetCategories}
            />
          </TabPanel>
        }
      </Tabs>
      <div className='flex'>
        <ExpenseSplit />
        <DistributionTypeExplanations />
      </div>
    </div>
  )
}

const Incomes = () => {
  const { values } = useFormikContext()
  const { budgets_attributes } = values

  return (
    <div className='m-4'>
      <h2 className='!mb-2'>Income</h2>
      <div className='flex md:w-1/2 gap-4'>
        {budgets_attributes.map(({ client_name }, idx) => (
          <div className='flex-1'>
            <NumberFieldWithLabel
              label={`${client_name}'s income`}
              name={`budgets_attributes[${idx}].gross_monthly_current_income`}
              placeholder='0'
            />
          </div>
        ))}
      </div>
    </div>
  )
}


const SimpleBudget = ({ minOldAgePension }) => {
  const { values } = useFormikContext()
  const { use_current_expense_for_retirement, net_monthly_retirement_budget_real } = values

  // Constants
  const MIN_OLD_AGE_PENSION = minOldAgePension
  const DEFAULT_PENSION_MESSAGE = `If your retirement expenses are entered as less than R${MIN_OLD_AGE_PENSION}, we'll automatically use R${MIN_OLD_AGE_PENSION} as the default. This reflects the minimum Old Age Pension (SASSA Grant), which we assume will be spent.`

  return (
    <div className='flex gap-4 md:w-3/4 lg:w-1/2 px-4'>
      <div className='flex-1'>
        <NumberFieldWithLabel
          label='Current expenses'
          name='net_monthly_current_budget_real'
          placeholder='0'
        />
        <CheckboxWithLabel
          label='Use current expenses for retirement'
          name='use_current_expense_for_retirement'
          size='small'
        />
      </div>
      <div className='flex-1'>
        <Hider withTransition
          show={!use_current_expense_for_retirement}
          render={() =>
            <NumberFieldWithLabel
              label='Retirement expenses'
              name='net_monthly_retirement_budget_real'
              placeholder='0'
              disabled={use_current_expense_for_retirement}
              subtext={(Number(net_monthly_retirement_budget_real) || 0) < MIN_OLD_AGE_PENSION ?
                DEFAULT_PENSION_MESSAGE :
                undefined
              }
            />
          }
        />
      </div>
    </div>
  )
}

const BudgetTabHeader = ({ selected, selectedTab, disableDetailedBudget }) => {
  const { readOnly } = useContext(ActionablesContext)
  return (
    <div className='flex-1'>
      <h2>Expenses</h2>
      <p>You are using <strong>{selected}</strong> expenses.</p>
      {disableDetailedBudget &&
        <p><strong>The detailed budget is only available on desktop.</strong></p>
      }
      {selectedTab === 'detailed' && !readOnly &&
        <CheckboxWithLabel
          label='Use current expenses for retirement'
          name='use_current_expense_for_retirement'
          size='small'
        />
      }
    </div>
  )
}

const ExpenseSplit = () => {
  const { values, setFieldValue } = useFormikContext()
  const { distribution_type, joint_budget_distributions_attributes } = values

  return (
    <div className='flex-1 md:w-1/2 mx-8 my-4'>
      <SelectWithLabel
        className='min-w-fit mb-4'
        name='distribution_type'
        label='Expense split'
        options={[
          {
            name: 'Custom', value: 'custom'
          },
          {
            name: 'Proportional by income', value: 'proportional_by_income'
          },
          {
            name: 'Proportional by disctretionary investments', value: 'proportional_by_discretionary_investments'
          }
        ]}
      />
      <Hider withTransition
        show={distribution_type === 'custom'}
        render={() =>
          <div className='flex gap-4'>
            {joint_budget_distributions_attributes.map(({ client_name }, idx) =>
              <div className='flex-1'>
                <NumberFieldWithLabel
                  label={`${client_name}'s portion(%)`}
                  name={`joint_budget_distributions_attributes[${idx}].value`}
                  placeholder='0'
                  numberFormatProps={{
                    isAllowed: (value) => {
                      const { floatValue } = value

                      return R(floatValue).value <= 100
                    }
                  }}
                  // User convenience: when one value is changed, the other is automatically updated
                  onChange={(e) => {
                    const { value } = e.target
                    const remainder = 100 - value
                    setFieldValue(`joint_budget_distributions_attributes[${idx}].value`, R(value).value || null)
                    setFieldValue(`joint_budget_distributions_attributes[${idx === 0 ? 1 : 0}].value`, R(remainder).value || null)
                  }}
                />
              </div>
            )}
          </div>
        }
      />
    </div>
  )
}

const DistributionTypeExplanations = () => {
  const { values } = useFormikContext()
  const { distribution_type } = values

  const explanation = distributionTypeExplanations[distribution_type]

  return (
    <div className={`flex-1 hidden md:block m-2 p-4`}>
      <p className='bold'>{explanation.name}</p>
      <p>{explanation.description}</p>
    </div>
  )
}
